import { SearchCalendar } from "@frontend/features/common/components/SearchCalendar";
import { Add, ArrowDropDown, ArrowForward, Delete, FlightLand, FlightTakeoff, Person, ReadMore, SwapHoriz, SyncAlt } from "@mui/icons-material"
import { Box, Button, IconButton, InputAdornment, Menu, MenuItem, styled, Switch, TextField, Typography, useTheme } from "@mui/material"
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

type AirTripTypes = 'roundTrip' | 'oneWay' | 'multidestination';

interface TripSegment  {
  id: string;
  origin: string;
  destination: string;
  date: string;
};

const returnTripTypeIcon = (value: AirTripTypes) => {
  if (value === 'roundTrip') {
    return SyncAlt
  } else if (value === 'oneWay') {
    return ArrowForward
  } else {
    return ReadMore
  }
}

const CustomSelector = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  gap: '6px'// si tienen q ser 6
}));

export const FlightSearchHeader = () => {
  const [tripType, setTripType] = useState<AirTripTypes>('roundTrip');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [segments, setSegments] = useState<TripSegment[]>([
    { id: uuid(), origin: "", destination: "", date: "" },
    { id: uuid(), origin: "", destination: "", date: "" },
  ]);
  const { t } = useTranslation();
  const theme = useTheme();
  const TripIcon = returnTripTypeIcon(tripType);

  const handleOpenTripTypeMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseTripTypeMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectTripType = (type: AirTripTypes) => {
    setTripType(type);
    handleCloseTripTypeMenu();
  };

  const addSegment = () => {
    setSegments(prevSegments => [...prevSegments, { id: uuid(), origin: "", destination: "", date: "" }]);
  };

  const removeSegment = (id: string) => {
    if (segments.length > 2) {
      setSegments(prevSegments => prevSegments.filter((segment) => segment.id !== id));
    }
  };

  return (
    <Box width="100%" display="flex" flexDirection="column" gap={2}>
      <Box display="flex" gap={1}>
        <CustomSelector onClick={handleOpenTripTypeMenu}>
          <TripIcon htmlColor={theme.palette.secondary.main} />
          <Typography variant="subtitle2" color="secondary">{t(`flights.searcher.${tripType}`)}</Typography>
          <ArrowDropDown htmlColor={theme.palette.secondary.main} />
        </CustomSelector>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseTripTypeMenu}>
          {(["oneWay","roundTrip",  "multidestination"] as AirTripTypes[]).map((type) => (
            <MenuItem key={type} onClick={() => handleSelectTripType(type)}>
              {t(`flights.searcher.${type}`)}
            </MenuItem>
          ))}
        </Menu>

        <CustomSelector>
          <Person fontSize="small" htmlColor={theme.palette.secondary.main} />
          <Typography variant="subtitle2" color="secondary">1</Typography>
          <ArrowDropDown htmlColor={theme.palette.secondary.main} />
        </CustomSelector>

      </Box>


      {/* ONE WAY */}
      {tripType === 'oneWay' && <Box display="flex" alignItems="center" gap={2}>
        <Box display="flex" alignItems="center" gap={1} flex={1}>
          <TextField
            fullWidth
            placeholder={t('flights.searcher.origin')}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FlightTakeoff />
                </InputAdornment>
              ),
            }}
          />
          <SwapHoriz htmlColor={theme.palette.secondary.main} />
          <TextField
            fullWidth
            size="small"
            placeholder={t('flights.searcher.destination')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FlightLand />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box display="flex" gap={2} flex={1}>
          <SearchCalendar
            //minDate={searchHotelData.from}
            label={t('flights.searcher.departureDate')}
          //value={searchHotelData.to}
          //onChange={setSearchToDate}
          />
        </Box>
      </Box>}

      {/* ROUND TRIP */}
      {tripType === 'roundTrip' && <Box display="flex" alignItems="center" gap={2}>
        <Box display="flex" alignItems="center" gap={1} flex={1}>
          <TextField
            fullWidth
            placeholder={t('flights.searcher.origin')}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FlightTakeoff />
                </InputAdornment>
              ),
            }}
          />
          <SwapHoriz htmlColor={theme.palette.secondary.main} />
          <TextField
            fullWidth
            size="small"
            placeholder={t('flights.searcher.destination')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FlightLand />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box display="flex" gap={2} flex={1}>
          <SearchCalendar
            //minDate={searchHotelData.from}
            label={t('flights.searcher.departureDate')}
          //value={searchHotelData.to}
          //onChange={setSearchToDate}
          />
          <SearchCalendar
            //minDate={searchHotelData.from}
            label={t('flights.searcher.arrivalDate')}
          //value={searchHotelData.to}
          //onChange={setSearchToDate}
          />
        </Box>
      </Box>}



      {/* Multidestination */}
      {tripType === "multidestination" && (
        <>
          {segments.map((segment, index) => (
            <Box key={segment.id} display="flex" alignItems="center" gap={2}>
              <Box display="flex" alignItems="center" gap={1} flex={1}>
                <TextField
                  fullWidth
                  placeholder={t("flights.searcher.origin")}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FlightTakeoff />
                      </InputAdornment>
                    ),
                  }}
                />
                <SwapHoriz htmlColor={theme.palette.secondary.main} />
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("flights.searcher.destination")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FlightLand />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box display="flex" gap={2}>
                <SearchCalendar label={t("flights.searcher.departureDate")} />
              </Box>
              {segments.length > 2 && (
                <IconButton onClick={() => removeSegment(segment.id)}>
                  <Delete color="error" />
                </IconButton>
              )}
            </Box>
          ))}
        
          <Button variant="outlined" color="secondary" onClick={addSegment} startIcon={<Add />}>
            {t("flights.searcher.addSection")}
          </Button>
        </>
      )}



      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <Switch />
          <Typography color="white" variant="body2">{t('flights.searcher.unknownDates')}</Typography>
        </Box>
        <Button variant="contained" color="secondary">{t('flights.searcher.search')}</Button>
      </Box>
    </Box>
  )
}